import { useState, useEffect } from 'react';
import { couponAPI } from '../../services/api';
import { Ticket, Plus, Edit2, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

const defaultForm = {
    code: '', description: '', discountType: 'percentage', discountValue: '',
    minOrderAmount: '', maxDiscountAmount: '', maxUses: '', maxUsesPerCustomer: '1',
    startDate: '', endDate: '', isActive: true
};

export default function CouponsAdmin() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [formBusy, setFormBusy] = useState(false);

    const fetchCoupons = async () => {
        setLoading(true);
        try {
            const res = await couponAPI.getAll();
            setCoupons(res.data || []);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { fetchCoupons(); }, []);

    const openCreate = () => {
        setEditItem(null);
        setForm(defaultForm);
        setShowModal(true);
    };

    const openEdit = (item) => {
        setEditItem(item);
        setForm({
            code: item.code || '',
            description: item.description || '',
            discountType: item.discountType || 'percentage',
            discountValue: item.discountValue || '',
            minOrderAmount: item.minOrderAmount || '',
            maxDiscountAmount: item.maxDiscountAmount || '',
            maxUses: item.maxUses || '',
            maxUsesPerCustomer: item.maxUsesPerCustomer || '1',
            startDate: item.startDate ? item.startDate.slice(0, 16) : '',
            endDate: item.endDate ? item.endDate.slice(0, 16) : '',
            isActive: item.isActive
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormBusy(true);
        try {
            const data = {
                ...form,
                discountValue: parseFloat(form.discountValue) || 0,
                minOrderAmount: parseFloat(form.minOrderAmount) || 0,
                maxDiscountAmount: form.discountType === 'percentage' && form.maxDiscountAmount ? parseFloat(form.maxDiscountAmount) : null,
                maxUses: form.maxUses ? parseInt(form.maxUses) : null,
                maxUsesPerCustomer: parseInt(form.maxUsesPerCustomer) || 1,
                startDate: form.startDate || null,
                endDate: form.endDate || null
            };
            if (editItem) {
                await couponAPI.update(editItem.id, data);
            } else {
                await couponAPI.create(data);
            }
            setShowModal(false);
            fetchCoupons();
        } catch (err) {
            alert(err.response?.data?.error || 'Hata oluştu');
        }
        setFormBusy(false);
    };

    const handleToggle = async (id) => {
        try {
            await couponAPI.toggleActive(id);
            fetchCoupons();
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu kuponu silmek istediğinize emin misiniz?')) return;
        try {
            await couponAPI.delete(id);
            fetchCoupons();
        } catch (err) { alert(err.response?.data?.error || 'Silinemedi'); }
    };

    const formatDiscount = (coupon) => {
        if (coupon.discountType === 'percentage') return `%${coupon.discountValue}`;
        return `${coupon.discountValue} TL`;
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '-';

    return (
        <div>
            <div className="admin-header">
                <h1><Ticket size={24} style={{verticalAlign:'middle', marginRight:8}} />Kupon Yönetimi</h1>
                <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Yeni Kupon</button>
            </div>

            {loading ? <div className="loading"><div className="spinner"></div></div> : (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Kod</th>
                                <th>İndirim</th>
                                <th>Min. Tutar</th>
                                <th>Kullanım</th>
                                <th>Tarih Aralığı</th>
                                <th>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.map(item => (
                                <tr key={item.id}>
                                    <td style={{fontWeight:700, letterSpacing:'1px', color:'var(--accent-primary)'}}>{item.code}</td>
                                    <td style={{fontWeight:600}}>{formatDiscount(item)}</td>
                                    <td>{item.minOrderAmount > 0 ? `${item.minOrderAmount} TL` : '-'}</td>
                                    <td>{item.usedCount} / {item.maxUses || '\u221e'}</td>
                                    <td style={{fontSize:'0.82rem', color:'var(--text-secondary)'}}>
                                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                                    </td>
                                    <td>
                                        <span className={`stock-badge ${item.isActive ? 'stock-in' : 'stock-out'}`}>
                                            {item.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{display:'flex', gap:'0.4rem'}}>
                                            <button className="btn-icon" onClick={() => handleToggle(item.id)} title={item.isActive ? 'Pasife Al' : 'Aktife Al'}>
                                                {item.isActive ? <ToggleRight size={18} style={{color:'var(--success)'}} /> : <ToggleLeft size={18} />}
                                            </button>
                                            <button className="btn-icon" onClick={() => openEdit(item)} title="Düzenle"><Edit2 size={16} /></button>
                                            <button className="btn-icon" onClick={() => handleDelete(item.id)} title="Sil" style={{color:'var(--danger)'}}><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {coupons.length === 0 && (
                                <tr><td colSpan="7" style={{textAlign:'center', padding:'2rem', color:'var(--text-muted)'}}>Henüz kupon eklenmemiş</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className="modal" style={{maxWidth:620}}>
                        <div className="modal-header">
                            <h2>{editItem ? 'Kuponu Düzenle' : 'Yeni Kupon'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body" style={{maxHeight:'70vh', overflowY:'auto'}}>
                                <div className="form-group">
                                    <label>Kupon Kodu *</label>
                                    <input className="form-control" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                                        placeholder="ÖRNEK: HOŞGELDIN10" required style={{letterSpacing:'2px', fontWeight:600}} />
                                </div>

                                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem'}}>
                                    <div className="form-group">
                                        <label>İndirim Tipi *</label>
                                        <select className="form-control" value={form.discountType} onChange={e => setForm({...form, discountType: e.target.value})}>
                                            <option value="percentage">Yüzde (%)</option>
                                            <option value="fixed">Sabit Tutar (TL)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>İndirim Değeri *</label>
                                        <input type="number" step="0.01" className="form-control" value={form.discountValue}
                                            onChange={e => setForm({...form, discountValue: e.target.value})} placeholder={form.discountType === 'percentage' ? 'örn: 10' : 'örn: 50'} required />
                                    </div>
                                </div>

                                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem'}}>
                                    <div className="form-group">
                                        <label>Min. Sipariş Tutarı (TL)</label>
                                        <input type="number" step="0.01" className="form-control" value={form.minOrderAmount}
                                            onChange={e => setForm({...form, minOrderAmount: e.target.value})} placeholder="0" />
                                    </div>
                                    {form.discountType === 'percentage' && (
                                        <div className="form-group">
                                            <label>Maks. İndirim (TL)</label>
                                            <input type="number" step="0.01" className="form-control" value={form.maxDiscountAmount}
                                                onChange={e => setForm({...form, maxDiscountAmount: e.target.value})} placeholder="Sınır yok" />
                                        </div>
                                    )}
                                </div>

                                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem'}}>
                                    <div className="form-group">
                                        <label>Toplam Kullanım Limiti</label>
                                        <input type="number" className="form-control" value={form.maxUses}
                                            onChange={e => setForm({...form, maxUses: e.target.value})} placeholder="Sınırsız" />
                                    </div>
                                    <div className="form-group">
                                        <label>Kişi Başı Limit</label>
                                        <input type="number" className="form-control" value={form.maxUsesPerCustomer}
                                            onChange={e => setForm({...form, maxUsesPerCustomer: e.target.value})} placeholder="1" />
                                    </div>
                                </div>

                                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem'}}>
                                    <div className="form-group">
                                        <label>Başlangıç Tarihi</label>
                                        <input type="datetime-local" className="form-control" value={form.startDate}
                                            onChange={e => setForm({...form, startDate: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Bitiş Tarihi</label>
                                        <input type="datetime-local" className="form-control" value={form.endDate}
                                            onChange={e => setForm({...form, endDate: e.target.value})} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Açıklama</label>
                                    <textarea className="form-control" rows={2} value={form.description}
                                        onChange={e => setForm({...form, description: e.target.value})} placeholder="Admin notu (opsiyonel)" />
                                </div>

                                <div className="form-group" style={{display:'flex', alignItems:'center'}}>
                                    <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                                        <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} /> Aktif
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
                                <button type="submit" className="btn btn-primary" disabled={formBusy}>
                                    {formBusy ? 'Kaydediliyor...' : (editItem ? 'Güncelle' : 'Oluştur')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
